import { SymbolLayerSpecification } from 'mapbox-gl'
import React, { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react'

import { EuiPanel, EuiTitle, EuiText, EuiInMemoryTable, EuiBasicTableColumn, SortDirection, EuiSpacer, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiLink, EuiButtonIcon } from '@elastic/eui'

import { BillboardGeo } from 'api/interfaces/Billboard'
import { AIPlanCustomSection, AIPlanStub } from 'api/interfaces/AIPlan'
import { useGetBillboardsByZipCodesQuery } from 'api/rtkQueryApi/platform/billboardApi'
import { MapBoxContainer } from 'components/mapBox/MapBoxContainer'
import { useMapBox } from 'components/mapBox/useMapBox'
import { calculateCenterPoint, calculateOptimalZoom } from 'utils/mapUtils'

import { useAIPlanBillboardZipCodes } from './useAIPlanProjectionsUtils'

export interface AIPlanBillboardLocationsProps {
  stub: AIPlanStub
  isCapturing?: boolean
  section?: AIPlanCustomSection
  editMode?: boolean
  onEdit?: () => void
}

export interface AIPlanBillboardLocationsRef {
  getMapCanvas: () => Promise<string | null>
  isMapReady: () => boolean
}

const AIPlanBillboardLocations = forwardRef<AIPlanBillboardLocationsRef, AIPlanBillboardLocationsProps>(({ stub, isCapturing, section, editMode, onEdit }, ref) => {
  const { mapContainerRef, initializeMap, setMarkers, flyTo, setInitialCenterAndZoom, returnToCenter, hasInitialCenter, isReady, isStyleLoaded, isLoading, hasError, error, extractMapCanvas, hideNavigationControls, showNavigationControls } = useMapBox()

  useEffect(() => {
    if (isReady) {
      if (isCapturing) {
        hideNavigationControls()
      } else {
        showNavigationControls()
      }
    }
  }, [isCapturing, isReady, hideNavigationControls, showNavigationControls])

  useImperativeHandle(
    ref,
    () => ({
      getMapCanvas: async () => {
        return await extractMapCanvas()
      },
      isMapReady: () => {
        return isStyleLoaded && isReady
      }
    }),
    [extractMapCanvas, isStyleLoaded, isReady]
  )

  const [selectedBillboardId, setSelectedBillboardId] = useState<string | null>(null)
  const [singleBillboardMode, setSingleBillboardMode] = useState<boolean>(false)

  const handleViewBillboardFromGrid = useCallback(
    (billboard: BillboardGeo) => {
      setSingleBillboardMode(true)
      setSelectedBillboardId(billboard.shortId)
      flyTo([billboard.longitude, billboard.latitude], 15)
    },
    [flyTo]
  )

  const handleResetView = useCallback(() => {
    setSelectedBillboardId(null)
    setSingleBillboardMode(false)
    returnToCenter()
  }, [returnToCenter])

  const zipCodes = useAIPlanBillboardZipCodes(stub)
  const { data: billboardData } = useGetBillboardsByZipCodesQuery({ zipCodes }, { skip: zipCodes.length === 0 })

  const sortedBillboardData = useMemo(() => {
    if (!billboardData) {
      return []
    }

    return [...billboardData]
      .sort((a, b) => (b.avgDailyImpressions || 0) - (a.avgDailyImpressions || 0))
      .sort((a, b) => {
        if (!a.zipCode && !b.zipCode) return 0
        if (!a.zipCode) return 1
        if (!b.zipCode) return -1
        return String(b.zipCode).localeCompare(String(a.zipCode))
      })
  }, [billboardData])

  useEffect(() => {
    if (!isReady || !sortedBillboardData || !sortedBillboardData.length) {
      return
    }

    let markersToShow = sortedBillboardData

    if (singleBillboardMode && selectedBillboardId) {
      markersToShow = sortedBillboardData.filter(billboard => billboard.shortId === selectedBillboardId)
    }

    const bbMarkers = markersToShow.map(billboard => ({ lat: billboard.latitude, lng: billboard.longitude, id: billboard.shortId }))
    setMarkers(bbMarkers, {
      clusterSettings: {
        cluster: !singleBillboardMode,
        radius: 50
      },
      unclusteredOptions: {
        type: 'symbol',
        layout: {
          'icon-image': 'billboard',
          'icon-size': 0.3,
          'icon-allow-overlap': false
        }
      } as unknown as SymbolLayerSpecification
    })

    if (!singleBillboardMode) {
      const geoPoints = sortedBillboardData.map(billboard => ({ lat: billboard.latitude, lng: billboard.longitude }))
      const centerPoint = calculateCenterPoint(geoPoints)
      const optimalZoom = calculateOptimalZoom(geoPoints)

      if (centerPoint) {
        setInitialCenterAndZoom([centerPoint.lng, centerPoint.lat], optimalZoom)
      }
    }
  }, [sortedBillboardData, setMarkers, setInitialCenterAndZoom, isReady, singleBillboardMode, selectedBillboardId])

  const pagination = {
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 20]
  }

  const billboardSorting = {
    allowNeutralSort: true,
    enableAllColumns: true,
    sort: {
      field: '',
      direction: SortDirection.DESC
    }
  }

  const billboardColumns: Array<EuiBasicTableColumn<BillboardGeo>> = [
    ...(!isCapturing
      ? [
          {
            field: 'location',
            name: 'Location',
            sortable: false,
            render: (value: any, billboard: BillboardGeo) => (
              <EuiLink onClick={() => handleViewBillboardFromGrid(billboard)} aria-label={`View billboard ${billboard.shortId} on map`}>
                View on Map
              </EuiLink>
            )
          } as EuiBasicTableColumn<BillboardGeo>
        ]
      : []),
    {
      field: 'route',
      name: 'Roadway',
      sortable: true
    },
    {
      field: 'city',
      name: 'City',
      sortable: true
    },
    {
      field: 'zipCode',
      name: 'Zip',
      sortable: true
    },
    {
      field: 'mediaOwner',
      name: 'Provider',
      sortable: true
    },
    {
      field: 'shortId',
      name: 'Billboard ID',
      sortable: true
    }
  ]

  if (section?.hideAll === true) {
    return null
  }

  const sectionTitle = section?.title ?? 'Billboard Locations'
  const sectionContent = section?.content ?? 'Digital billboards provide high-visibility exposure in key geographic areas. The map and table below show all available billboard locations within your targeted regions.'
  const showTitle = section?.showTitle ?? true

  if (!billboardData || billboardData.length === 0) {
    return (
      <EuiPanel paddingSize='l'>
        {!isCapturing && showTitle && (
          <EuiTitle size='m'>
            <h2>{sectionTitle}</h2>
          </EuiTitle>
        )}
        {!isCapturing && <EuiSpacer size='l' />}
        <EuiEmptyPrompt
          iconType='visMapRegion'
          title={<h3>No billboard locations found</h3>}
          body={
            <p>
              No billboard inventory was found for the targeted zip codes in this campaign plan.
              {zipCodes.length === 0 ? ' Please ensure your campaign has location targeting configured.' : ` Searched in ${zipCodes.length} zip code${zipCodes.length > 1 ? 's' : ''}: ${zipCodes.join(', ')}`}
            </p>
          }
        />
      </EuiPanel>
    )
  }

  return (
    <EuiPanel paddingSize='l'>
      {!isCapturing && (
        <>
          <EuiFlexGroup alignItems='center' gutterSize='s'>
            {showTitle && (
              <EuiFlexItem grow={false}>
                <EuiTitle size='m'>
                  <h2>{sectionTitle}</h2>
                </EuiTitle>
              </EuiFlexItem>
            )}
            {editMode && onEdit && (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEdit} aria-label='Edit Billboard Locations section' />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          <EuiSpacer size='s' />
          <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
            {sectionContent}
          </EuiText>
          <EuiSpacer size='l' />
        </>
      )}

      <EuiInMemoryTable items={sortedBillboardData} columns={billboardColumns} pagination={pagination} sorting={billboardSorting} responsive={true} hasActions={false} />

      <EuiSpacer size='l' />

      <div style={isCapturing ? { width: 2000, height: 500, marginLeft: 'auto', marginRight: 'auto' } : { maxWidth: 1000, height: 400, marginLeft: 'auto', marginRight: 'auto', marginBottom: '2rem' }}>
        {!isCapturing && (
          <>
            <EuiFlexGroup justifyContent='spaceBetween' gutterSize='s'>
              <EuiFlexItem grow={false}>
                <EuiLink color='success' onClick={handleResetView} disabled={!hasInitialCenter} aria-label='Show all billboards'>
                  Show All
                </EuiLink>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiLink color='success' onClick={handleResetView} disabled={!hasInitialCenter} aria-label='Reset map view'>
                  Reset View
                </EuiLink>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size='s' />
          </>
        )}

        <MapBoxContainer mapContainerRef={mapContainerRef} initializeMap={initializeMap} isReady={isReady} isLoading={isLoading} hasError={hasError} error={error} />
      </div>
    </EuiPanel>
  )
})

AIPlanBillboardLocations.displayName = 'AIPlanBillboardLocations'

export default AIPlanBillboardLocations
